import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import {
  uninstallingFocusTemplate,
  installFocusTemplate
} from 'store/reducer/focus-mode/extra';
import {
  installHabitPack,
  uninstallHabitPack,
  upsertHabitPack
} from 'store/reducer//habit-pack/extra';
import {
  setCurrentViewedPack,
  updateIsHabitPackPdfShowed
} from 'store/reducer/habit-pack/slice';
import {
  updateRoutinePlayerActivities,
  updateRoutinePlayingActivity
} from 'store/reducer/setting/slice';
import { usePackAndFocusEditor } from 'components/pack-and-focus-editor';
import {
  ACTIVITY_TYPES,
  MODAL_TYPES,
  ROUTINE_PLAYING_TYPE
} from 'constants/general';
import { PAGES, ROUTES } from 'constants/routes';
import {
  HabitPackFormat,
  PackAndFocusEntityType,
  PackEditorMode
} from 'enums';
import { v4 as uuid } from 'uuid';
import { DetailsPageWrapperContext } from 'dashboard/wrappers/DetailsPageWrapper';
import { usePackPreviewContext } from 'components/pack-focus-picker/PackPreviewContext';
import { updateConfirmActionModal } from 'store/reducer/modal/slice';
import Spinner from '../Spinner';
import { t } from 'services/i18n.service';
import { habitPackActionButtonSelector } from 'store/reducer/habit-pack/selectors';
import { cloneDeep } from 'utils/helpers/cloneDeep';
import { BaseButtonXs } from 'components/ui/BaseButton';

export default function HabitPackActionButton({
  text
}: {
  readonly text: string;
}) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { openEditor } = usePackAndFocusEditor();
  const {
    currentViewedPack,
    installingPack,
    uninstallingPack,
    currentViewedFocusTemplate,
    isFocusTemplateInstalling,
    isFocusTemplateUninstalling,
    focus_modes,
    isUserSettingsModified,
    areSettingsValid
  } = useAppSelectorShallow(habitPackActionButtonSelector);

  const { page } = useContext(DetailsPageWrapperContext);
  const packPreview = usePackPreviewContext();
  const isEitherInstallOrUninstallInAction =
    (installingPack ||
      uninstallingPack ||
      isFocusTemplateInstalling ||
      isFocusTemplateUninstalling) &&
    (t('market.install') === text || t('market.uninstall') === text);
  const handleActionClick = () => {
    switch (text) {
      case t('back'):
        dispatch(setCurrentViewedPack(null));
        break;
      case t('market.install'):
        if (page === PAGES.HABIT_PACK && currentViewedPack) {
          if (!areSettingsValid) {
            toast.error(
              t(
                'you_can_only_install_or_uninstall_habit_packs_when_your_routine_settings_are_valid'
              ),
              { position: 'bottom-center' }
            );
          } else if (isUserSettingsModified) {
            dispatch(
              updateConfirmActionModal({
                modalType: MODAL_TYPES.CONFIRM_HABIT_PACK_INSTALLATION,
                cancelTitle: t('btn_cancel'),
                confirmTitle: t('continue'),
                description: t('you_have_unsaved_changes_in_your_settings'),
                showConfirmModal: true,
                data: { packId: currentViewedPack.id }
              })
            );
          } else {
            dispatch(installHabitPack(currentViewedPack.id));
          }
        } else {
          currentViewedFocusTemplate?.id &&
            dispatch(installFocusTemplate(currentViewedFocusTemplate.id));
        }
        break;
      case t('market.uninstall'):
        if (page === PAGES.HABIT_PACK && currentViewedPack) {
          if (!areSettingsValid) {
            toast.error(
              t(
                'you,_can_only_install_or_uninstall_habit_packs_when_your_routine_settings_are_valid'
              ),
              { position: 'bottom-center' }
            );
          } else if (isUserSettingsModified) {
            dispatch(
              updateConfirmActionModal({
                modalType: MODAL_TYPES.CONFIRM_HABIT_PACK_UNINSTALLATION,
                cancelTitle: t('btn_cancel'),
                confirmTitle: t('continue'),
                description: t('you_have_unsaved_changes_in_your_settings'),
                showConfirmModal: true,
                data: { packId: currentViewedPack.id }
              })
            );
          } else {
            dispatch(uninstallHabitPack(currentViewedPack.id));
          }
        } else {
          const focusMode = focus_modes.find(
            (focusMode) =>
              focusMode.focus_mode_template_id ===
                currentViewedFocusTemplate?.id ||
              focusMode.id === currentViewedFocusTemplate?.id
          );
          focusMode && dispatch(uninstallingFocusTemplate(focusMode.id));
        }
        break;
      case t('market.delete'):
        if (page === PAGES.HABIT_PACK) {
          dispatch(
            updateConfirmActionModal({
              modalType: MODAL_TYPES.CONFIRM_ACTION_DELETE_HABIT_PACK,
              confirmTitle: t('market.delete'),
              description: t('packs.delete_habit_pack'),
              showConfirmModal: true
            })
          );
        } else {
          dispatch(
            updateConfirmActionModal({
              modalType: MODAL_TYPES.CONFIRM_ACTION_DELETE_FOCUS_TEMPLATE,
              cancelTitle: t('btn_cancel'),
              confirmTitle: t('market.delete'),
              description: t('focus_mode.delete_focus_template'),
              showConfirmModal: true
            })
          );
        }
        break;
      case t('market.edit'):
        if (packPreview?.isPreview) {
          packPreview.onClose();
        }
        if (page === PAGES.HABIT_PACK && currentViewedPack) {
          openEditor({
            type: PackAndFocusEntityType.HABIT_PACK,
            mode: PackEditorMode.EDIT,
            pack: currentViewedPack
          });
        } else if (currentViewedFocusTemplate) {
          openEditor({
            type: PackAndFocusEntityType.FOCUS_TEMPLATE,
            mode: PackEditorMode.EDIT,
            template: currentViewedFocusTemplate
          });
        }
        break;
      case t('market.play'):
        if (currentViewedPack?.standalone_activities?.length === 0) {
          toast.error(t('market.habit_pack_not_having_an_activity'));
        } else {
          if (currentViewedPack?.standalone_activities) {
            const activity_sequence_id = uuid();
            const activities = currentViewedPack?.standalone_activities.map(
              (activity) => ({ ...activity, activity_sequence_id })
            );
            dispatch(
              updateRoutinePlayerActivities({
                type: ACTIVITY_TYPES.STANDALONE,
                activities
              })
            );
            dispatch(
              updateRoutinePlayingActivity({
                type: ROUTINE_PLAYING_TYPE.CURRENT,
                activity: activities[0]
              })
            );
            navigate(`../${ROUTES.ROUTINE_PLAYER}`, {
              state: {
                routine_mode: HabitPackFormat.STANDALONE
              }
            });
          }
        }
        break;
      case t('market.generate_pdf'):
        dispatch(updateIsHabitPackPdfShowed(true));
        break;
      case t('duplicate'):
      case t('market.duplicate'):
        if (currentViewedPack) {
          let clonedPack = cloneDeep(currentViewedPack);
          clonedPack.id = uuid();
          const morning_activities = clonedPack?.morning_activities?.map(
            (activity) => ({
              ...activity,
              id: uuid()
            })
          );
          const break_activities = clonedPack?.break_activities?.map(
            (activity) => {
              const choices = activity?.choices ?? [];
              if (choices.length > 0) {
                choices.map((choice) => ({ ...choice, id: uuid() }));
              }
              return {
                ...activity,
                id: uuid(),
                choices
              };
            }
          );
          const evening_activities = clonedPack?.evening_activities?.map(
            (activity) => ({
              ...activity,
              id: uuid()
            })
          );
          const standalone_activities = clonedPack?.standalone_activities?.map(
            (activity) => ({
              ...activity,
              id: uuid()
            })
          );
          clonedPack = {
            ...clonedPack,
            morning_activities,
            break_activities,
            evening_activities,
            standalone_activities
          };
          dispatch(upsertHabitPack(clonedPack));
        }
    }
  };

  return isEitherInstallOrUninstallInAction ? (
    <Spinner minHeight='h-fit' />
  ) : (
    <BaseButtonXs
      title={text}
      onClick={handleActionClick}
      disabled={isEitherInstallOrUninstallInAction}
      variant={text === t('back') ? 'secondary' : 'primary'}
      additionalStyles='~min-w-28/32 justify-center'
    />
  );
}
